import { pwInput, pwLabel, pwSectionTitle } from "./policyWizardFieldStyles";
import {
  POLICY_EMPLOYMENT_TYPE_OPTIONS,
  POLICY_LOCATION_OPTIONS,
  APPLICABILITY_CLEAR_FIELDS,
} from "./policyApplicabilityOptions";

/**
 * Reusable Applicability step — onboarding-style layout (rounded-lg, grid masters).
 * Use in policy wizards and salary structure assignment.
 */
export default function PolicyApplicabilityStepSection({
  darkMode = false,
  policy,
  setPolicy,
  branchesList = [],
  departmentsList = [],
  designationsList = [],
  gradesList = [],
  categoriesList = [],
  subDepartmentsList = [],
  locationOptions = POLICY_LOCATION_OPTIONS,
  employmentTypeOptions = POLICY_EMPLOYMENT_TYPE_OPTIONS,
  onToggleEmploymentType,
  title = "Applicability",
  allEmployeesLabel = "Select All (Applicable to all employees)",
  employmentTypesHelp = "Select one or more to limit by employment type. Leave all unchecked to apply to every employment type within the scope above.",
  showEmploymentTypes = true,
  showEmploymentTypesHelp = true,
  /** When false, Location is hidden (e.g. salary structure — Location is not a Masters entry). */
  showLocation = true,
  children,
}) {
  const handleAllEmployees = (checked) => {
    setPolicy((p) => ({
      ...p,
      allEmployees: checked,
      ...(checked ? APPLICABILITY_CLEAR_FIELDS : {}),
    }));
  };

  const disabled = !!policy.allEmployees;
  const inputCls = `${pwInput(darkMode)} ${disabled ? "opacity-50" : ""}`;

  const masterLabel = (item) =>
    item?.name || item?.label || item?.title || String(item?.id ?? "");

  return (
    <div>
      <h4 className={pwSectionTitle(darkMode)}>{title}</h4>
      <label className={`flex items-center text-sm mb-3 cursor-pointer ${darkMode ? "text-gray-200" : "text-[hsl(var(--ds-fg))]"}`}>
        <input
          type="checkbox"
          checked={!!policy.allEmployees}
          onChange={(e) => handleAllEmployees(e.target.checked)}
          className="mr-2 rounded border-[hsl(var(--ds-border))]"
        />
        {allEmployeesLabel}
      </label>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={pwLabel(darkMode)}>Branch</label>
          <select
            disabled={disabled}
            value={policy.branch || ""}
            onChange={(e) => setPolicy((p) => ({ ...p, branch: e.target.value }))}
            className={inputCls}
          >
            <option value="">All Branches</option>
            {branchesList?.map((b) => (
              <option key={b.id} value={b.id}>
                {masterLabel(b)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={pwLabel(darkMode)}>Department</label>
          <select
            disabled={disabled}
            value={policy.department || ""}
            onChange={(e) => setPolicy((p) => ({ ...p, department: e.target.value }))}
            className={inputCls}
          >
            <option value="">All Departments</option>
            {departmentsList?.map((d) => (
              <option key={d.id} value={d.id}>
                {masterLabel(d)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={pwLabel(darkMode)}>Designation</label>
          <select
            disabled={disabled}
            value={policy.designation || ""}
            onChange={(e) => setPolicy((p) => ({ ...p, designation: e.target.value }))}
            className={inputCls}
          >
            <option value="">All Designations</option>
            {designationsList?.map((d) => (
              <option key={d.id} value={d.id}>
                {masterLabel(d)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={pwLabel(darkMode)}>Grade</label>
          <select
            disabled={disabled}
            value={policy.grade || ""}
            onChange={(e) => setPolicy((p) => ({ ...p, grade: e.target.value }))}
            className={inputCls}
          >
            <option value="">All Grades</option>
            {gradesList?.map((g) => (
              <option key={g.id} value={g.id}>
                {masterLabel(g)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={pwLabel(darkMode)}>Category</label>
          <select
            disabled={disabled}
            value={policy.category || ""}
            onChange={(e) => setPolicy((p) => ({ ...p, category: e.target.value }))}
            className={inputCls}
          >
            <option value="">All Categories</option>
            {categoriesList?.map((c) => (
              <option key={c.id} value={c.id}>
                {masterLabel(c)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={pwLabel(darkMode)}>Sub Department</label>
          <select
            disabled={disabled}
            value={policy.subDepartment || ""}
            onChange={(e) => setPolicy((p) => ({ ...p, subDepartment: e.target.value }))}
            className={inputCls}
          >
            <option value="">All Sub Departments</option>
            {subDepartmentsList?.map((s) => (
              <option key={s.id} value={s.id}>
                {masterLabel(s)}
              </option>
            ))}
          </select>
        </div>
        {showLocation && (
          <div>
            <label className={pwLabel(darkMode)}>Location</label>
            <select
              disabled={disabled}
              value={policy.location || ""}
              onChange={(e) => setPolicy((p) => ({ ...p, location: e.target.value }))}
              className={inputCls}
            >
              {locationOptions.map((loc) => (
                <option key={loc.value} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {showEmploymentTypes && (
        <div className="mt-3">
          <label className={pwLabel(darkMode)}>Employment types</label>
          {showEmploymentTypesHelp ? (
            <p className={`text-xs mb-2 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              {disabled
                ? "Optional: limit All employees to specific employment types. Leave all unchecked to include every employment type (that case overlaps any other all-employee structure)."
                : employmentTypesHelp}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {employmentTypeOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center text-sm cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={
                    Array.isArray(policy.employmentTypes) &&
                    policy.employmentTypes.includes(opt.value)
                  }
                  onChange={() => onToggleEmploymentType?.(opt.value)}
                  className="mr-2 cursor-pointer rounded border-[hsl(var(--ds-border))]"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {children}
    </div>
  );
}
